import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Spin, Table, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { DAY_FORMAT, ORDER_STATUSES_DISPLAY } from '../constants';
import { getMyPartnerRoute } from '../modules/partners/api';

/**
 * OVO-002 — Partner-scoped route detail.
 *
 * Hits the dedicated /partners/me/routes/:id endpoint, which returns
 * only the caller's stops (filtered by Order.partnerId) with relative
 * orderNumber (1..N of the partner's slice, not the route's global
 * positions). The map + polyline + other-partner aggregates are
 * intentionally NOT exposed by the backend, so even a tampered client
 * can't see anything they shouldn't.
 *
 * Routed at /partner/routes/:id (partner_viewer role only).
 */

// OVO-130 — "Xh Ym" client-service duration.
const formatMins = (mins) => {
  if (mins == null || mins <= 0) return '-';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const STATUS_COLOR = {
  COMPLETED: 'green',
  COLLECTED: 'blue',
  PICKUP_ARRIVED: 'blue',
  DELIVERY_ARRIVED: 'blue',
  IN_TRANSIT: 'blue',
  SKIPPED: 'orange',
  DELIVERY_FAILED: 'red',
  PICKUP_FAILED: 'red',
  ERROR: 'red',
};

export default function PartnerRouteDetail() {
  const { t } = useTranslation(['routes']);

  // #112 — "Planned 09:12 / Actual 09:31". An actual badge on another day
  // than the route shows its date.
  const renderStopTime = (plannedStr, actualTs) => {
    const m = actualTs ? moment(actualTs) : null;
    const actual =
      m && m.isValid() ? m.format(route?.date && m.isSame(moment(route.date), 'day') ? 'HH:mm' : 'DD MMM HH:mm') : null;
    if (!plannedStr && !actual) return '-';
    return (
      <div style={{ lineHeight: 1.35 }}>
        {plannedStr && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t('plannedLabel')} {plannedStr}
          </div>
        )}
        {actual && (
          <div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('actualLabel')} </span>
            {actual}
          </div>
        )}
      </div>
    );
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { response, error: err } = await getMyPartnerRoute(id);
      if (canceled) return;
      setLoading(false);
      if (err) {
        setError(err?.data?.message || 'Could not load the route');
        return;
      }
      setRoute(response);
    })();
    return () => {
      canceled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
        <Alert type="error" showIcon message="Route unavailable" description={error} />
        <Button style={{ marginTop: 16 }} onClick={() => navigate('/partner/routes')}>
          Back to routes
        </Button>
      </div>
    );
  }

  if (!route) return null;

  const steps = route.steps || [];

  return (
    <div className="PartnerRouteDetail" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/partner/routes')}>
          Back to routes
        </Button>
      </div>

      <h2 style={{ marginTop: 0 }}>Route #{route.id}</h2>

      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <Field label="Date" value={route.date ? moment(route.date).format(DAY_FORMAT) : '-'} />
          <Field label="Driver" value={route.driverName || '-'} />
          <Field label="Vehicle" value={route.fleetName || '-'} />
          {/* OVO-138 — the route-level lifecycle status (DISPATCHED / IN_PROGRESS
              / COMPLETED) is OVO-internal vocabulary and, on a mutualised route,
              describes the whole route rather than this partner's slice — both
              meaningless and slightly leaky to a partner. Removed here; the
              partner-meaningful signal is the per-order delivery status shown in
              the Stops table below. */}
          {/* OVO-130 — client service time (first pickup → last delivery),
              the metric an hourly client is billed on. The shift window
              (bike-out → bike-in) is payroll data and no longer exposed
              to the partner. */}
          <Field
            label={<Tooltip title="From your first pickup to your last delivery">Service time</Tooltip>}
            value={formatMins(route.clientServiceMins)}
          />
          {route.priseDeServiceMins != null && (
            <Field
              label={
                <Tooltip title="Your share of the bike pick-up / return + start & close overhead on this route">
                  Prise de service
                </Tooltip>
              }
              value={formatMins(route.priseDeServiceMins)}
            />
          )}
          {/* OVO-106 — Fair-share CO2 (proportional to this partner's stops). */}
          {route.partnerShareKm > 0 && (
            <Field
              label={<Tooltip title="Your share of the route, proportional to your stops">Your distance</Tooltip>}
              value={`${route.partnerShareKm.toFixed(1)} km`}
            />
          )}
          {route.co2 && (
            <Field
              label={<Tooltip title={`${route.co2.gPerKm} g CO₂-eq per km · Mobitool v3.1 factor`}>Your CO₂</Tooltip>}
              value={`${route.co2.kg.toFixed(2)} kg`}
            />
          )}
          {route.co2?.savedKg > 0 && (
            <Field
              label={
                <Tooltip title={`Compared to an average diesel car at ${route.co2.baselineGPerKm} g CO₂-eq/km`}>
                  CO₂ saved
                </Tooltip>
              }
              value={<span style={{ color: '#2e7d32', fontWeight: 600 }}>−{route.co2.savedKg.toFixed(2)} kg</span>}
            />
          )}
        </div>
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Showing your ${steps.length} stop${steps.length === 1 ? '' : 's'} on this route.`}
        description="Stop numbers are relative to your orders. If this route is shared with other partners, their stops are not shown."
      />

      {steps.length === 0 ? (
        <Empty description="No stops on this route belong to your partner." />
      ) : (
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={steps}
          columns={[
            {
              title: '#',
              key: 'orderNumber',
              width: 60,
              render: (_, s) => <strong>{s.orderNumber}</strong>,
            },
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              width: 110,
              render: (t) => (
                <Tag color={String(t).toLowerCase() === 'pickup' ? 'cyan' : 'geekblue'}>{String(t).toLowerCase()}</Tag>
              ),
            },
            {
              title: 'Stop',
              key: 'stop',
              render: (_, s) => {
                const isPickup = String(s.type).toLowerCase() === 'pickup';
                const name = isPickup ? s.order?.pickupName : s.order?.deliveryName;
                const address = isPickup ? s.order?.pickupAddress : s.order?.deliveryAddress;
                return (
                  <div>
                    <div style={{ fontWeight: 500 }}>{name || '-'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{address || '-'}</div>
                  </div>
                );
              },
            },
            {
              title: 'Order',
              key: 'order',
              width: 300,
              // Under the code, the order's OTHER leg (pickup row → where it
              // goes, delivery row → where it came from) — the same
              // correlation the admin route page shows; it's the partner's
              // own order data, so OVO-002-safe.
              render: (_, s) => {
                const o = s.order;
                if (!o?.orderCode) return '-';
                const isPickup = String(s.type).toLowerCase() === 'pickup';
                const isDelivery = String(s.type).toLowerCase() === 'delivery';
                const otherName = isPickup ? o.deliveryName : isDelivery ? o.pickupName : null;
                const otherAddr = isPickup ? o.deliveryAddress : isDelivery ? o.pickupAddress : null;
                return (
                  <div>
                    <Link to={`/partner/orders/${o.orderCode}`}>{o.orderCode}</Link>
                    {(otherName || otherAddr) && (
                      <div
                        style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}
                        title={[otherName, otherAddr].filter(Boolean).join(' — ')}
                      >
                        <span style={{ color: 'var(--text-muted)' }}>
                          {isPickup ? t('infoWindow.goesTo') : t('infoWindow.comesFrom')}{' '}
                        </span>
                        {otherName && <strong>{otherName}</strong>}
                        {otherAddr && <span style={{ color: 'var(--text-muted)' }}> · {otherAddr}</span>}
                      </div>
                    )}
                  </div>
                );
              },
            },
            // #112 — Planned (optimizer ETA) / Actual (driver's badge) per stop,
            // the same two-line cell as the admin route page (OVO-140). The
            // actual times are the partner's own stops' logs — OVO-002-safe.
            {
              title: t('arrivalTimeCol'),
              key: 'arrival',
              width: 120,
              render: (_, s) => renderStopTime(s.arrivalTime, s.actualArrivalTime),
            },
            {
              title: t('finishTimeCol'),
              key: 'finish',
              width: 120,
              render: (_, s) => renderStopTime(s.finishTime, s.actualFinishTime),
            },
            {
              title: t('stopStatus'),
              key: 'status',
              width: 130,
              render: (_, s) => {
                // This stop's own leg: a pickup row shows the pickup status.
                const isPickup = String(s.type).toLowerCase() === 'pickup';
                const status = isPickup ? s.order?.pickupStatus || s.order?.status : s.order?.status;
                const display = ORDER_STATUSES_DISPLAY?.[status] || status || '-';
                return <Tag color={STATUS_COLOR[status] || 'default'}>{display}</Tag>;
              },
            },
          ]}
        />
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
}
